import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const PAGES = [
  { key: "websites", label: "Websites", path: "/websites", text: "Portfolio videos, website packages and Shopify builds. Hidden pages keep all their content, pricing and Stripe products — nothing is deleted." },
  { key: "social", label: "Social Media", path: "/social", text: "Monthly plans, content calendar preview and the inquiry form. While hidden, the inquiry form stays reachable at /inquire so leads can still write to you." },
  { key: "two_point_oh", label: "2.0", path: "/2-0", text: "Business Aligned 2.0 — the write-up and links to www.thebusinessaligned.com for clients who want one-on-one guidance (The Review, The Alignment, The Build)." },
];

export default function AdminPages() {
  const { pages, setPages } = useSite();
  const [busy, setBusy] = useState("");

  const toggle = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.patch("/admin/site/pages", { pages: { [key]: !pages[key] } });
      setPages(data.pages);
      toast.success(`${PAGES.find((p) => p.key === key).label} is now ${data.pages[key] ? "visible" : "hidden"} on the site.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="glass rounded-2xl p-6 sm:p-8" data-testid="admin-pages">
      <span className="eyebrow">Site pages</span>
      <h2 className="f-display text-2xl tracking-tight mt-2 mb-1">Show or hide sections of the site</h2>
      <p className="text-sm text-[#8C7562] leading-relaxed max-w-2xl mb-7">
        Hidden pages disappear from the navigation, footer, home page and account prompts. All data stays saved — flip them back on any time. As the owner you can still preview a hidden page from here.
      </p>
      <div className="flex flex-col gap-4">
        {PAGES.map((p) => {
          const on = pages[p.key];
          return (
            <div key={p.key} className="rounded-xl border border-[#E8DCCB] bg-[#FFFDF7] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`admin-page-row-${p.key}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="f-display text-xl tracking-tight">{p.label}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 ${on ? "bg-[#A8785A]/10 text-[#A8785A]" : "bg-[#E8DCCB] text-[#8C7562]"}`} data-testid={`admin-page-status-${p.key}`}>
                    {on ? "Visible" : "Hidden"}
                  </span>
                </div>
                <p className="text-sm text-[#8C7562] leading-relaxed mt-1.5">{p.text}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to={`${p.path}?preview=1`} className="btn-ghost !py-2.5 !px-4 !text-[11px]" data-testid={`admin-page-preview-${p.key}`}>
                  <ExternalLink size={12} /> Preview
                </Link>
                <button onClick={() => toggle(p.key)} disabled={busy === p.key} data-testid={`admin-page-toggle-${p.key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors disabled:opacity-60 ${on ? "border border-[#E8DCCB] text-[#8C7562] hover:border-[#A8785A] hover:text-[#A8785A]" : "bg-[#A8785A] text-[#FFFDF7] hover:bg-[#8C6349]"}`}>
                  {on ? <><EyeOff size={13} /> Hide page</> : <><Eye size={13} /> Show page</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
